import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { buildUrl } from '../../../lib/api';
import { APP_QUERY } from '../../../lib/env';
import { t } from '../../../lib/i18n';
import { isNewBidSoundEnabled, playNewBidSound } from '../../../lib/sound';
import { toast } from '../../../lib/toast';
import { DEALS_QUEUE_QUERY_KEY } from '../../deals/api';
import {
  type AmountChangedEvent,
  appendNewBidEvent,
  type NewBidEvent,
  parseAdminStreamEvent,
  type RelayChangeEvent,
  relayChangeAudience,
} from '../new-bid-events';
import { formatChf, relayStatusTranslationKey } from '../utils';
import { BIDS_QUERY_KEY, RELAY_QUEUE_QUERY_KEY } from './use-bid-mutations';

/** How long a "new bid" stays on screen before it fades out. */
const VISIBLE_MS = 8_000;
/** A toast that carries a call-to-action deserves the same window. */
const TOAST_MS = 8_000;

export interface AdminBidStreamOptions {
  /**
   * The signed-in admin's id (`GET /users/me` `_id` — the same string the API
   * writes as `who` on relay audit rows). A relay transition the frame
   * attributes to this very operator is already acknowledged by the mutation's
   * own toast, so it is not announced again. Only the audit row names its
   * actor; a change-stream frame does not, and is then announced in neutral
   * wording rather than blamed on "another operator".
   */
  selfAdminId?: string;
}

export interface AdminBidStreamState {
  /** The last few captured bids, newest last (feeds the persistent NewBidAlert). */
  events: NewBidEvent[];
  /** True between `open` and the first `error` of the underlying EventSource. */
  connected: boolean;
  /** When the stream last went down (null while connected, or before the first open). */
  disconnectedSince: number | null;
}

/**
 * Live admin bid firehose — subscribes to `GET /api/bids/stream` (admin-only
 * SSE, already served by api-node's AdminStreamController) and surfaces newly
 * captured client bids.
 *
 * This is the monorepo home of the web's `AdminNewBidNotification`, which the
 * consolidation dropped: the API side survived, the consumer did not, so the
 * admin SPA had no realtime at all. On a bid-relay desk that is load-bearing —
 * an unnoticed bid is a bid that never reaches the real auction before its
 * tender closes.
 *
 * Every captured bid now also raises a toast with an «Відкрити» action that
 * lands on the bid, and — when the operator opted in on the settings screen —
 * a chime. A relayStatus transition (a colleague working the same queue, or
 * the platform auto-cancelling) raises a quieter informational toast, no
 * sound — from the change-stream update AND from the `relay.*` audit row, so
 * it keeps working when `OUTBOX_ENABLED=true` idles the change stream. A
 * client raising their own bid raises the same quiet toast with the new
 * amount (BUG-20) — it is not a status change and not a new bid.
 *
 * The stream cookie rides on `withCredentials` (the NextAuth session cookie is
 * shared by host across ports; api-node's CORS runs with credentials: true).
 * A new bid also invalidates the bids queries, so open lists refresh without a
 * manual reload.
 *
 * MOUNT ONCE (the app shell does). Each call opens its own EventSource, so a
 * second consumer would double every toast and chime — read `connected` and
 * `events` from the shell's single instance instead.
 */
export function useAdminBidStream(options: AdminBidStreamOptions = {}): AdminBidStreamState {
  const [events, setEvents] = useState<NewBidEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [disconnectedSince, setDisconnectedSince] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // The EventSource effect must not re-subscribe when the caller re-renders
  // with a fresh options object, so the self id is read through a ref.
  const selfAdminIdRef = useRef(options.selfAdminId);
  selfAdminIdRef.current = options.selfAdminId;

  useEffect(() => {
    if (typeof EventSource === 'undefined') return undefined;

    // `cfApp=admin`: authenticate by the admin session (EventSource sends no headers).
    const source = new EventSource(buildUrl('bids/stream', APP_QUERY), { withCredentials: true });

    const handleOpen = () => {
      setConnected(true);
      setDisconnectedSince(null);
    };
    const handleError = () => {
      setConnected(false);
      setDisconnectedSince((since) => since ?? Date.now());
    };

    const invalidateBidQueries = () => {
      void queryClient.invalidateQueries({ queryKey: BIDS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: RELAY_QUEUE_QUERY_KEY });
      // «Угоди» is a read model over the same bids: every tab and search.
      void queryClient.invalidateQueries({ queryKey: DEALS_QUEUE_QUERY_KEY });
    };

    const openAction = (bidId: string) => ({
      label: t('admin.stream.open'),
      onClick: () => void navigate({ to: '/bids/$id', params: { id: bidId } }),
    });

    const announceNewBid = (event: NewBidEvent) => {
      setEvents((queue) => appendNewBidEvent(queue, event));
      toast.success(
        t('admin.stream.newBid', {
          amount: formatChf(event.bidAmount),
          car: event.carLabel ?? event.carId,
        }),
        { duration: TOAST_MS, action: openAction(event.bidId) },
      );
      // Fire-and-forget here; only the settings Test button reads the outcome.
      // The preference is per operator (BUG-36): read it under the signed-in admin's id.
      if (isNewBidSoundEnabled(selfAdminIdRef.current)) void playNewBidSound();
      invalidateBidQueries();
    };

    const announceRelayChange = (event: RelayChangeEvent) => {
      // Lists must refresh regardless of who moved the bid.
      invalidateBidQueries();

      const audience = relayChangeAudience(event, selfAdminIdRef.current);
      if (audience === 'self') return;

      toast.info(
        t(audience === 'other' ? 'admin.stream.relayChangedByOther' : 'admin.stream.relayChanged', {
          car: event.carLabel ?? event.carId ?? event.bidId,
          status: t(relayStatusTranslationKey(event.relayStatus)),
        }),
        { duration: TOAST_MS, action: openAction(event.bidId) },
      );
    };

    // BUG-20: a client raising their own bid (`bid.updated`, `isNewBid:false`)
    // is neither a new bid (no chime, no banner) nor a relay transition — the
    // desk hears the new amount. Nobody clicked on the admin side, so there is
    // no audience to suppress for.
    const announceAmountChange = (event: AmountChangedEvent) => {
      invalidateBidQueries();
      toast.info(
        t('admin.stream.bidAmountChanged', {
          car: event.carLabel ?? event.carId ?? event.bidId,
          amount: formatChf(event.bidAmount),
        }),
        { duration: TOAST_MS, action: openAction(event.bidId) },
      );
    };

    const handleMessage = (event: MessageEvent<string>) => {
      const parsed = parseAdminStreamEvent(event.data);
      if (!parsed) return;
      if (parsed.kind === 'new-bid') {
        const { kind: _kind, ...newBid } = parsed;
        announceNewBid(newBid);
        return;
      }
      if (parsed.kind === 'amount-changed') {
        announceAmountChange(parsed);
        return;
      }
      announceRelayChange(parsed);
    };

    source.addEventListener('open', handleOpen);
    source.addEventListener('error', handleError);
    source.addEventListener('message', handleMessage as EventListener);

    return () => {
      source.removeEventListener('open', handleOpen);
      source.removeEventListener('error', handleError);
      source.removeEventListener('message', handleMessage as EventListener);
      source.close();
    };
  }, [navigate, queryClient]);

  // Drop each event once it has had its time on screen.
  useEffect(() => {
    if (events.length === 0) return undefined;
    const timer = setTimeout(() => setEvents([]), VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [events]);

  return { events, connected, disconnectedSince };
}
